import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import TabletMacIcon from '@mui/icons-material/TabletMac';
import HubIcon from '@mui/icons-material/Hub';
import {
  AuditActorType,
  AuditCategory,
  IAuditEvent,
  useAuditSearch,
} from '../hooks/reactQuerys/AuditQueries';
import {
  actorText,
  changeText,
  entityLabel,
  eventHeadline,
  sourceLabel,
} from '../generic/auditText';
import { CustomCircularProgress } from '../generic/CustomCircularProgress';

/** Protokollsuche über alle Entitäten (#1330, Phase 6d).
 *
 * Die Ansichten an Vorgang, Mandant, Aufgabe und Grundbuchblatt beantworten
 * „was ist mit DIESER Akte passiert?". Hier geht es um die andere Richtung —
 * „wer hat wann etwas getan?" und „wo taucht dieser Name auf?". Das ist die
 * Frage, die bei einer Auskunftsanfrage oder einem Verdacht gestellt wird, und
 * sie lässt sich in keiner Akte beantworten.
 */

const ACTOR_ICON: Record<AuditActorType, React.ReactElement> = {
  user: <PersonIcon fontSize='inherit' />,
  client: <HomeWorkIcon fontSize='inherit' />,
  system: <SettingsSuggestIcon fontSize='inherit' />,
  device: <TabletMacIcon fontSize='inherit' />,
  integration: <HubIcon fontSize='inherit' />,
};

const ACTOR_FILTER: { value: AuditActorType | ''; label: string }[] = [
  { value: '', label: 'Alle' },
  { value: 'user', label: 'Mitarbeiter' },
  { value: 'client', label: 'Mandanten' },
  { value: 'device', label: 'Geräte' },
  { value: 'system', label: 'Automatik' },
  { value: 'integration', label: 'Fremdsysteme' },
];

/** Entitätsarten, nach denen sich zu filtern lohnt — die mit eigener Ansicht. */
const ENTITY_FILTER = [
  '',
  'LegalTransaction',
  'LegalClient',
  'Task',
  'Document',
  'Invoice',
  'Grundbuchblatt',
  'LegalPhase',
  'LegalRequirement',
  'User',
];

const ROWS = 50;

const ZeitZelle: React.FC<{ iso: string }> = ({ iso }) => {
  const datum = new Date(iso);
  if (Number.isNaN(datum.getTime())) return <>—</>;
  return (
    <>
      {datum.toLocaleDateString('de-DE')}
      <Typography variant='caption' color='text.secondary' sx={{ ml: 0.75 }}>
        {datum.toLocaleTimeString('de-DE', {
          hour: '2-digit',
          minute: '2-digit',
        })}
      </Typography>
    </>
  );
};

const Zeile: React.FC<{ event: IAuditEvent }> = ({ event }) => (
  <TableRow hover>
    <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
      <ZeitZelle iso={event.OccurredAt} />
    </TableCell>
    <TableCell sx={{ verticalAlign: 'top' }}>
      <Stack direction='row' spacing={0.75} alignItems='center'>
        <Box
          sx={{ fontSize: '1rem', display: 'flex', color: 'text.secondary' }}
        >
          {ACTOR_ICON[event.ActorType]}
        </Box>
        <Typography variant='body2'>{actorText(event)}</Typography>
      </Stack>
      <Typography variant='caption' color='text.secondary'>
        {sourceLabel(event.Source)}
        {event.SourceDetail ? ` · ${event.SourceDetail}` : ''}
      </Typography>
    </TableCell>
    <TableCell sx={{ verticalAlign: 'top' }}>
      <Chip
        size='small'
        variant='outlined'
        label={entityLabel(event.EntityType)}
        sx={{ height: 20, fontSize: '0.7rem' }}
      />
      <Typography variant='caption' color='text.secondary' sx={{ ml: 0.75 }}>
        #{event.EntityId}
      </Typography>
    </TableCell>
    <TableCell sx={{ verticalAlign: 'top' }}>
      <Typography variant='body2'>{eventHeadline(event)}</Typography>
      {(event.Changes ?? []).slice(0, 4).map((change, index) => (
        <Typography
          key={`${event.idAuditEvent}-${change.field}-${index}`}
          variant='caption'
          color='text.secondary'
          sx={{ display: 'block', wordBreak: 'break-word' }}
        >
          {changeText(change)}
        </Typography>
      ))}
      {(event.Changes ?? []).length > 4 && (
        <Typography variant='caption' color='text.secondary'>
          … {(event.Changes ?? []).length - 4} weitere
        </Typography>
      )}
    </TableCell>
  </TableRow>
);

export const AuditSearchOverview: React.FC = () => {
  const [suchfeld, setSuchfeld] = useState('');
  const [suche, setSuche] = useState('');
  const [actorType, setActorType] = useState<AuditActorType | ''>('');
  const [entityType, setEntityType] = useState('');
  const [technisch, setTechnisch] = useState(false);
  const [vonDatum, setVonDatum] = useState('');
  const [bisDatum, setBisDatum] = useState('');
  const [page, setPage] = useState(1);

  const category: AuditCategory | 'all' = technisch ? 'all' : 'domain';

  const { auditEvents, auditCount, isLoadingAudit, isErrorAudit } =
    useAuditSearch({
      page,
      rowsPerPage: ROWS,
      category,
      actorType,
      entityType,
      search: suche,
      dateFrom: vonDatum,
      dateTo: bisDatum,
    });

  const seiten = Math.max(1, Math.ceil(auditCount / ROWS));

  const suchen = () => {
    setSuche(suchfeld.trim());
    setPage(1);
  };

  const zuruecksetzen = useMemo(
    () => () => {
      setSuchfeld('');
      setSuche('');
      setActorType('');
      setEntityType('');
      setVonDatum('');
      setBisDatum('');
      setPage(1);
    },
    []
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant='h5'>Protokoll</Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
        Änderungen über alle Akten hinweg. Die Ansichten am Vorgang, Mandanten
        oder an der Aufgabe zeigen, was mit dieser einen Akte passiert ist —
        hier steht, wer wann etwas getan hat.
      </Typography>

      <Paper variant='outlined' sx={{ p: 2, mt: 2 }}>
        <Stack
          direction='row'
          spacing={1.5}
          alignItems='center'
          flexWrap='wrap'
          useFlexGap
        >
          <TextField
            size='small'
            label='Suche'
            placeholder='Name, Bezeichnung, Ereignisart'
            value={suchfeld}
            onChange={e => setSuchfeld(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') suchen();
            }}
            sx={{ minWidth: 260 }}
          />
          <Tooltip title='Suchen'>
            <IconButton onClick={suchen} size='small'>
              <SearchIcon />
            </IconButton>
          </Tooltip>

          <FormControl size='small' sx={{ minWidth: 160 }}>
            <InputLabel>Ausgelöst von</InputLabel>
            <Select
              label='Ausgelöst von'
              value={actorType}
              onChange={e => {
                setActorType(e.target.value as AuditActorType | '');
                setPage(1);
              }}
            >
              {ACTOR_FILTER.map(o => (
                <MenuItem key={o.value || 'alle'} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ minWidth: 170 }}>
            <InputLabel>Art</InputLabel>
            <Select
              label='Art'
              value={entityType}
              onChange={e => {
                setEntityType(e.target.value);
                setPage(1);
              }}
            >
              {ENTITY_FILTER.map(t => (
                <MenuItem key={t || 'alle'} value={t}>
                  {t ? entityLabel(t) : 'Alle'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size='small'
            type='date'
            label='von'
            value={vonDatum}
            onChange={e => {
              setVonDatum(e.target.value);
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size='small'
            type='date'
            label='bis'
            value={bisDatum}
            onChange={e => {
              setBisDatum(e.target.value);
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
          />

          <Chip
            size='small'
            variant={technisch ? 'filled' : 'outlined'}
            color={technisch ? 'primary' : 'default'}
            label='technische Änderungen'
            onClick={() => {
              setTechnisch(!technisch);
              setPage(1);
            }}
          />
          <Chip
            size='small'
            variant='outlined'
            label='Filter zurücksetzen'
            onClick={zuruecksetzen}
          />
        </Stack>
      </Paper>

      <Box sx={{ mt: 2 }}>
        {isLoadingAudit && <CustomCircularProgress />}

        {isErrorAudit && (
          <Typography variant='body2' color='error'>
            Das Protokoll konnte nicht geladen werden.
          </Typography>
        )}

        {!isLoadingAudit && !isErrorAudit && auditEvents.length === 0 && (
          <Typography variant='body2' color='text.secondary'>
            Keine Einträge für diese Auswahl.
          </Typography>
        )}

        {!isLoadingAudit && auditEvents.length > 0 && (
          <>
            <Typography variant='caption' color='text.secondary'>
              {auditCount.toLocaleString('de-DE')} Einträge
            </Typography>
            <Paper variant='outlined' sx={{ mt: 0.5, overflowX: 'auto' }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Zeitpunkt</TableCell>
                    <TableCell>Ausgelöst von</TableCell>
                    <TableCell>Betroffen</TableCell>
                    <TableCell>Änderung</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditEvents.map(event => (
                    <Zeile key={event.idAuditEvent} event={event} />
                  ))}
                </TableBody>
              </Table>
            </Paper>

            {seiten > 1 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Stack alignItems='center'>
                  <Pagination
                    count={seiten}
                    page={page}
                    onChange={(_, wert) => setPage(wert)}
                    size='small'
                  />
                </Stack>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
